/**
 * Expense Mutation Hooks
 *
 * Hooks for creating, updating, and deleting expenses.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../../infrastructure/api/client";
import {
  ApiError,
  type ExpenseResponseDto,
  type CreateExpenseDto,
  type UpdateExpenseDto,
} from "@tabipla/shared";
import { expenseKeys, sessionKeys } from "../queryKeys";
import { invalidateExpenseQueries } from "../invalidation";
import { calculateEqualShares } from "./useExpenseCalculations";

/**
 * Create a new expense
 *
 * Uses optimistic updates for better UX.
 * Pass paidByName in the mutation variables for proper optimistic display.
 */
export function useCreateExpense(groupId: string, sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateExpenseDto & { paidByName?: string }) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { paidByName, ...expense } = input;
      const response = await apiClient.post<ExpenseResponseDto>(
        `/api/groups/${groupId}/sessions/${sessionId}/expenses`,
        expense
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create expense");
      }
      return response.data;
    },
    onMutate: async (input) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: expenseKeys.session(groupId, sessionId) });

      // Snapshot previous value
      const previousExpenses = queryClient.getQueryData<ExpenseResponseDto[]>(
        expenseKeys.session(groupId, sessionId)
      );

      // Create optimistic expense with temporary ID
      const tempId = `temp-${Date.now()}`;
      const optimisticExpense: ExpenseResponseDto = {
        id: tempId,
        sessionId,
        paidById: input.paidById ?? "",
        paidByType: input.paidByType ?? "user",
        paidByName: input.paidByName ?? "",
        amount: input.amount,
        currency: input.currency ?? "JPY",
        description: input.description,
        category: input.category,
        date: input.date,
        shares: input.shares.map((s) => ({
          ...s,
          memberName: "",
        })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Optimistically add the expense
      if (previousExpenses) {
        queryClient.setQueryData<ExpenseResponseDto[]>(expenseKeys.session(groupId, sessionId), [
          optimisticExpense,
          ...previousExpenses,
        ]);
      }

      return { previousExpenses, tempId };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousExpenses) {
        queryClient.setQueryData(expenseKeys.session(groupId, sessionId), context.previousExpenses);
      }
    },
    onSettled: () => {
      invalidateExpenseQueries(queryClient, groupId, sessionId);
      // Also invalidate session to update totalExpenses/expenseCount
      queryClient.invalidateQueries({ queryKey: sessionKeys.group(groupId) });
    },
  });
}

/**
 * Update an existing expense
 *
 * Uses optimistic updates for better UX.
 */
export function useUpdateExpense(groupId: string, sessionId: string, expenseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (expense: UpdateExpenseDto) => {
      const response = await apiClient.put<ExpenseResponseDto>(
        `/api/groups/${groupId}/sessions/${sessionId}/expenses/${expenseId}`,
        expense
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update expense");
      }
      return response.data;
    },
    onMutate: async (expense) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: expenseKeys.session(groupId, sessionId) });

      // Snapshot previous value
      const previousExpenses = queryClient.getQueryData<ExpenseResponseDto[]>(
        expenseKeys.session(groupId, sessionId)
      );

      // Optimistically update only the basic fields (not shares which has different structure)
      if (previousExpenses) {
        queryClient.setQueryData<ExpenseResponseDto[]>(
          expenseKeys.session(groupId, sessionId),
          previousExpenses.map((e) =>
            e.id === expenseId
              ? {
                  ...e,
                  amount: expense.amount ?? e.amount,
                  description: expense.description ?? e.description,
                  category: expense.category ?? e.category,
                  date: expense.date ?? e.date,
                  updatedAt: new Date().toISOString(),
                }
              : e
          )
        );
      }

      return { previousExpenses };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousExpenses) {
        queryClient.setQueryData(expenseKeys.session(groupId, sessionId), context.previousExpenses);
      }
    },
    onSettled: () => {
      invalidateExpenseQueries(queryClient, groupId, sessionId);
      queryClient.invalidateQueries({ queryKey: sessionKeys.group(groupId) });
    },
  });
}

/**
 * Delete an expense
 *
 * Uses optimistic updates for better UX.
 */
export function useDeleteExpense(groupId: string, sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (expenseId: string) => {
      const response = await apiClient.delete<null>(
        `/api/groups/${groupId}/sessions/${sessionId}/expenses/${expenseId}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete expense");
      }
      return expenseId;
    },
    onMutate: async (expenseId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: expenseKeys.session(groupId, sessionId) });

      // Snapshot previous value
      const previousExpenses = queryClient.getQueryData<ExpenseResponseDto[]>(
        expenseKeys.session(groupId, sessionId)
      );

      // Optimistically remove the expense
      if (previousExpenses) {
        queryClient.setQueryData<ExpenseResponseDto[]>(
          expenseKeys.session(groupId, sessionId),
          previousExpenses.filter((expense) => expense.id !== expenseId)
        );
      }

      return { previousExpenses };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousExpenses) {
        queryClient.setQueryData(expenseKeys.session(groupId, sessionId), context.previousExpenses);
      }
    },
    onSettled: () => {
      invalidateExpenseQueries(queryClient, groupId, sessionId);
      queryClient.invalidateQueries({ queryKey: sessionKeys.group(groupId) });
    },
  });
}

/**
 * Split an expense equally among members
 *
 * Encapsulates business logic and API call in the application layer.
 */
export function useSplitExpenseEqually(groupId: string, sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      expense,
      members,
    }: {
      expense: ExpenseResponseDto;
      members: Array<{ memberId: string; memberType: "user" | "guest"; joinedAt: string }>;
    }) => {
      const expenseDate = new Date(expense.date);
      const shares = calculateEqualShares(expense.amount, members, expenseDate);

      if (shares.length === 0) {
        throw new Error("No eligible members for splitting");
      }

      const response = await apiClient.put<ExpenseResponseDto>(
        `/api/groups/${groupId}/sessions/${sessionId}/expenses/${expense.id}`,
        {
          amount: expense.amount,
          description: expense.description,
          category: expense.category,
          date: expense.date,
          shares,
        }
      );

      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to split expense");
      }
      return response.data;
    },
    onSuccess: () => {
      invalidateExpenseQueries(queryClient, groupId, sessionId);
      queryClient.invalidateQueries({ queryKey: sessionKeys.group(groupId) });
    },
  });
}
